import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AULA_1_DATA } from '../data/course-data';

@Component({
  selector: 'app-aula1-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-10">
      <!-- Breadcrumb Navigation -->
      <div class="flex items-center justify-between">
        <button 
          type="button" 
          (click)="navigateBack.emit()" 
          class="inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold text-[#F27D26] border border-white/10 transition-all cursor-pointer">
          <span class="material-symbols-outlined text-sm">arrow_back</span>
          <span>Voltar ao Menu de Aulas</span>
        </button>
      </div>

      <!-- Main Section Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold border border-[#F27D26]/30">
            <span class="material-symbols-outlined text-sm">build</span>
            <span>{{ data.badge }}</span>
          </span>
          <span class="inline-flex items-center space-x-1 px-3 py-1 rounded-full bg-white/5 text-zinc-300 text-xs font-mono font-medium border border-white/10">
            <span class="material-symbols-outlined text-sm mr-1 text-[#F27D26]">schedule</span>
            <span>{{ data.duration }}</span>
          </span>
        </div>

        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ data.title }}
        </h2>
        <p class="text-zinc-400 text-sm max-w-3xl leading-relaxed">
          {{ data.subtitle }}
        </p>
      </div>

      <!-- Como Funciona a Dinâmica (4 Passos) -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
          <span class="material-symbols-outlined text-[#F27D26] text-2xl">route</span>
          <h3 class="text-xl font-bold text-white tracking-tight">Como Funciona a Dinâmica</h3>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          @for (step of data.steps; track step.step) {
            <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 hover:border-[#F27D26]/40 transition-all flex flex-col justify-between space-y-3">
              <div>
                <div class="w-10 h-10 rounded-lg bg-[#F27D26]/10 border border-[#F27D26]/30 flex items-center justify-center text-[#F27D26] mb-3">
                  <span class="material-symbols-outlined text-xl">{{ step.icon }}</span>
                </div>
                <h4 class="font-bold text-white text-sm mb-1.5">{{ step.title }}</h4>
                <p class="text-xs text-zinc-400 leading-relaxed">{{ step.description }}</p>
              </div>
              <div class="pt-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-[#F27D26]">
                <span>ETAPA 0{{ step.step }}</span>
                <span class="material-symbols-outlined text-sm">arrow_forward</span>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Exemplos de Dores em Redes -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
          <span class="material-symbols-outlined text-[#F27D26] text-2xl">warning</span>
          <div>
            <h3 class="text-xl font-bold text-white tracking-tight">Exemplos de Dores em Redes</h3>
            <p class="text-xs text-zinc-400 mt-0.5">Problemas frequentes identificados no mercado para inspirar o grupo</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          @for (example of data.painExamples; track example.title) {
            <div class="p-4 rounded-xl bg-[#0A0A0B] border border-white/10 hover:border-[#F27D26]/30 transition-all flex items-start space-x-3">
              <div class="p-2 rounded-lg bg-[#F27D26]/10 text-[#F27D26] shrink-0 mt-0.5 border border-[#F27D26]/20">
                <span class="material-symbols-outlined text-lg">{{ example.icon }}</span>
              </div>
              <div>
                <h4 class="text-xs font-bold text-white mb-1">{{ example.title }}</h4>
                <p class="text-xs text-zinc-400 leading-relaxed">{{ example.description }}</p>
              </div>
            </div>
          }
        </div>

        <!-- Botão Ver Tudo como Comparativo -->
        <div class="pt-3 border-t border-white/10 flex justify-end">
          <button 
            type="button" 
            (click)="showComparative = !showComparative" 
            class="px-4 py-2 rounded-xl bg-[#F27D26]/10 hover:bg-[#F27D26]/20 text-[#F27D26] border border-[#F27D26]/30 text-xs font-semibold transition-all flex items-center space-x-2 cursor-pointer">
            <span class="material-symbols-outlined text-sm">compare_arrows</span>
            <span>Ver Tudo (Comparativo de Dores vs Soluções)</span>
          </button>
        </div>

        @if (showComparative) {
          <div class="p-5 rounded-xl bg-[#0A0A0B] border border-[#F27D26]/30 space-y-3 animate-fadeIn">
            <h4 class="text-xs font-mono font-bold text-[#F27D26] uppercase">Comparativo Sintético das 3 Dores Principais</h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div class="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                <p class="font-bold text-white">1. Wi-Fi Instável</p>
                <p class="text-zinc-400 mt-1">Impacto: Parada de operações. Solução: SD-WAN + Wi-Fi 6 com monitoramento preditivo NOC.</p>
              </div>
              <div class="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                <p class="font-bold text-white">2. Falta de Segurança</p>
                <p class="text-zinc-400 mt-1">Impacto: Invasões e perda de dados. Solução: ZTNA (Zero Trust Network Access) e Firewall gerenciado.</p>
              </div>
              <div class="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                <p class="font-bold text-white">3. Suporte Lento</p>
                <p class="text-zinc-400 mt-1">Impacto: Frustração dos usuários. Solução: SLA garantido de 15 min e portal automatizado com IA.</p>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- 📖 Recursos e Leitura de Apoio -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
          <span class="material-symbols-outlined text-[#F27D26] text-2xl">menu_book</span>
          <div>
            <h3 class="text-xl font-bold text-white tracking-tight">📖 Recursos e Leitura de Apoio</h3>
            <p class="text-xs text-zinc-400 mt-0.5">Material complementar para aprofundar os conceitos abordados na aula:</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          @for (res of data.resources; track res.title) {
            <a 
              [href]="res.url" 
              target="_blank" 
              rel="noopener noreferrer"
              class="p-5 rounded-xl bg-[#0A0A0B] border border-white/10 hover:border-[#F27D26]/50 transition-all flex items-start justify-between group cursor-pointer">
              <div class="flex items-start space-x-3">
                <div class="p-2.5 rounded-lg bg-[#F27D26]/10 text-[#F27D26] border border-[#F27D26]/30 group-hover:bg-[#F27D26] group-hover:text-black transition-colors shrink-0 mt-0.5">
                  <span class="material-symbols-outlined text-xl">{{ res.icon }}</span>
                </div>
                <div>
                  <h4 class="text-sm font-bold text-white group-hover:text-[#F27D26] transition-colors mb-1">{{ res.title }}</h4>
                  <p class="text-xs text-zinc-400 leading-relaxed">{{ res.description }}</p>
                </div>
              </div>
              <span class="material-symbols-outlined text-zinc-500 group-hover:text-[#F27D26] text-sm shrink-0 ml-2">open_in_new</span>
            </a>
          }
        </div>
      </div>

      <!-- 🚀 Próximos Passos -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-4">
        <div class="flex items-center space-x-3 border-b border-white/10 pb-4">
          <span class="material-symbols-outlined text-[#F27D26] text-2xl">rocket_launch</span>
          <h3 class="text-xl font-bold text-white tracking-tight">🚀 Próximos Passos</h3>
        </div>

        <ul class="space-y-3 pt-1">
          @for (next of data.nextSteps; track next) {
            <li class="flex items-start space-x-3 p-3.5 rounded-xl bg-[#0A0A0B] border border-white/10 text-sm text-zinc-200">
              <span class="material-symbols-outlined text-[#F27D26] text-base shrink-0 mt-0.5">check_box</span>
              <span class="leading-relaxed font-medium">{{ next }}</span>
            </li>
          }
        </ul>
      </div>
    </section>
  `
})
export class Aula1SectionComponent {
  navigateBack = output<void>();
  data = AULA_1_DATA;
  showComparative = false;
}
